import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Plus, Trash2, ListChecks, Loader2 } from 'lucide-react';
import { authHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Modal de configuração (CRUD por tenant) dos itens do Checklist Pós-Manutenção.
export default function ChecklistConfigModal({ open, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/os-checklist-templates?incluir_inativos=true`, { headers: authHeaders() });
      if (r.ok) setItems(await r.json());
    } catch (e) {
      toast.error('Erro ao carregar itens');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const addItem = async () => {
    if (!newLabel.trim()) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/os-checklist-templates`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ label: newLabel.trim(), ordem: items.length }),
      });
      if (r.ok) {
        setNewLabel('');
        await load();
        toast.success('Item adicionado');
        onSaved && onSaved();
      } else {
        const d = await r.json();
        toast.error(typeof d.detail === 'string' ? d.detail : 'Erro ao adicionar');
      }
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/os-checklist-templates/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (r.ok) {
        await load();
        toast.success('Item removido');
        onSaved && onSaved();
      } else {
        toast.error('Erro ao remover');
      }
    } catch (e) {
      toast.error('Erro ao remover');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" data-testid="checklist-config-modal">
      <div className="bg-[#141414] border border-gray-800 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-800 sticky top-0 bg-[#141414]">
          <div className="flex items-center gap-2">
            <ListChecks size={18} className="text-green-400" />
            <h3 className="text-base font-semibold text-white">Configurar Checklist Pós-Manutenção</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="checklist-config-close"><X size={20} /></button>
        </div>

        <div className="p-4 space-y-3">
          <p className="text-xs text-gray-500">
            Estes itens aparecem em todas as O.S. de manutenção do seu time. Se nenhum item for cadastrado, um conjunto padrão é usado.
          </p>

          <div className="flex gap-2">
            <input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addItem()}
              placeholder="Novo item do checklist..."
              data-testid="checklist-config-new-input"
              className="flex-1 px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
            />
            <button
              onClick={addItem}
              disabled={saving || !newLabel.trim()}
              data-testid="checklist-config-add"
              className="px-3 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg text-sm disabled:opacity-40 flex items-center gap-1"
            >
              <Plus size={16} /> Add
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="animate-spin text-gray-500" /></div>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">Nenhum item configurado — o padrão será usado.</p>
          ) : (
            <ul className="space-y-2" data-testid="checklist-config-list">
              {items.map((it) => (
                <li key={it.id} className="flex items-center gap-2 p-2 rounded-lg bg-[#0A0A0A] border border-gray-800" data-testid={`checklist-config-item-${it.id}`}>
                  <span className="flex-1 text-sm text-gray-200">{it.label}</span>
                  <button onClick={() => removeItem(it.id)} className="text-red-400 hover:text-red-300" data-testid={`checklist-config-remove-${it.id}`}><Trash2 size={16} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
